'use client';

import { FC, memo } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useTranslations } from 'next-intl';
import {
  Card,
  CardCollapseBody,
  CardSubtitle,
  CardTitle,
  CardTitleHeader,
  CollapseToggle,
} from '@/features/pages/profile/wlas-widget/components/WlasTabsPanel/tabs/configuration/styled';
import {
  WLAS_PARAMS,
  WLAS_SECTIONS,
} from '@/features/pages/profile/wlas-widget/components/WlasTabsPanel/tabs/configuration/constants';
import { WlasValues } from '@/features/pages/profile/wlas-widget/components/WlasTabsPanel/tabs/configuration/utils';
import { WlasChevronIcon } from './components/WlasSection/WlasChevronIcon';
import { WlasSection } from './components/WlasSection';
import { WlasFooterActions } from '../WlasFooterActions';

interface WlasParamsCardProps {
  values: WlasValues;
  onChange: (id: string, value: string | boolean) => void;
  isDirty: boolean;
  isPreviewDirty: boolean;
  isSaving: boolean;
  onSave: () => void;
  onPreview: () => void;
  onReset: () => void;
}

const SECTION_PARAMS = WLAS_SECTIONS.map(({ key, labelKey }) => ({
  key,
  labelKey,
  params: WLAS_PARAMS.filter((p) => p.section === key && !p.readonly),
})).filter(({ params }) => params.length > 0);

export const WlasParamsCard: FC<WlasParamsCardProps> = memo(
  ({ values, onChange, isDirty, isPreviewDirty, isSaving, onSave, onPreview, onReset }) => {
    const t = useTranslations('wlasWidget');
    const [open, setOpen] = useLocalStorage('wlas-card-params-open', true);

    return (
      <Card>
        <CardTitleHeader onClick={() => setOpen((v) => !v)}>
          <div>
            <CardTitle>{t('paramsCard.title')}</CardTitle>
            <CardSubtitle>{t('paramsCard.subtitle')}</CardSubtitle>
          </div>
          <CollapseToggle
            type="button"
            aria-expanded={open}
            aria-controls="wlas-params-body"
            onClick={(e) => {
              e.stopPropagation();
              setOpen((v) => !v);
            }}
          >
            <WlasChevronIcon open={open} />
          </CollapseToggle>
        </CardTitleHeader>
        {open && (
          <CardCollapseBody>
            <div id="wlas-params-body">
              {SECTION_PARAMS.map(({ key, labelKey, params }, index) => (
                <WlasSection
                  key={key}
                  labelKey={labelKey}
                  params={params}
                  values={values}
                  onChange={onChange}
                  isFirst={index === 0}
                />
              ))}
            </div>
            <WlasFooterActions
              isDirty={isDirty}
              isPreviewDirty={isPreviewDirty}
              isSaving={isSaving}
              onSave={onSave}
              onPreview={onPreview}
              onReset={onReset}
            />
          </CardCollapseBody>
        )}
      </Card>
    );
  },
);
WlasParamsCard.displayName = 'WlasParamsCard';
